import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

// ── Animated call transcript mockup ───────────────────────────────────────────

const DEMO_TURNS = [
  { speaker: 'agent', text: "Thank you for calling Bright Smile Dental, this is Maria, how can I help you?" },
  { speaker: 'user',  text: "Hi Maria, do you ever lose patients to missed calls after hours?" },
  { speaker: 'agent', text: "Probably, yeah — but we have voicemail. Can I ask what company you're with?" },
  { speaker: 'user',  text: "I called your clinic at 8pm yesterday. I haven't heard back." },
  { speaker: 'agent', text: "...Fair point. We do get backed up. What exactly would this involve for the doctor?" },
  { speaker: 'user',  text: "Two minutes. I have a live demo — this AI answers every call and books directly to your calendar." },
  { speaker: 'agent', text: "Hold on — let me see if Dr. Park has a moment between patients." },
];

const SCORE_REVEAL = { hook: 8, clarity: 7, objections: 8, close: 6 };

function AnimatedDemo() {
  const [turns, setTurns] = useState([]);
  const [showScore, setShowScore] = useState(false);
  const [scores, setScores] = useState({});
  const bottomRef = useRef(null);
  const idx = useRef(0);
  const timer = useRef(null);

  useEffect(() => {
    function addTurn() {
      if (idx.current < DEMO_TURNS.length) {
        setTurns(prev => [...prev, DEMO_TURNS[idx.current]]);
        idx.current++;
        const delay = idx.current < DEMO_TURNS.length ? 1800 : 2200;
        timer.current = setTimeout(addTurn, delay);
      } else {
        // Reveal scores one by one
        const keys = Object.keys(SCORE_REVEAL);
        keys.forEach((k, i) => {
          setTimeout(() => {
            setScores(prev => ({ ...prev, [k]: SCORE_REVEAL[k] }));
            if (i === keys.length - 1) setShowScore(true);
          }, i * 300 + 400);
        });
        // Loop after 6 seconds
        timer.current = setTimeout(() => {
          idx.current = 0;
          setTurns([]);
          setShowScore(false);
          setScores({});
          timer.current = setTimeout(addTurn, 800);
        }, 7000);
      }
    }
    timer.current = setTimeout(addTurn, 600);
    return () => clearTimeout(timer.current);
  }, []);

  useEffect(() => {
    if (bottomRef.current) {
      bottomRef.current.parentElement.scrollTo({
        top: bottomRef.current.parentElement.scrollHeight,
        behavior: 'smooth',
      });
    }
  }, [turns]);

  const scoreColor = s => s >= 7 ? '#16A34A' : s >= 5 ? '#D97706' : '#DC2626';

  return (
    <div style={D.demoWrap}>
      <div style={D.demoHeader}>
        <div style={D.demoDot} />
        <span style={D.demoTitle}>Live Practice Call</span>
      </div>
      <div style={D.transcript}>
        {turns.filter(Boolean).map((t, i) => (
          <div key={i} style={{ ...D.turn, ...(t.speaker === 'user' ? D.turnUser : D.turnAgent) }}>
            <span style={{ ...D.turnLabel, ...(t.speaker === 'user' ? D.labelUser : D.labelAgent) }}>
             {t.speaker === 'user' ? 'YOU' : 'MARIA'}
            </span>
            <span style={D.turnText}>{t.text}</span>
          </div>
        ))}
        <div ref={bottomRef} />
      </div>
      {Object.keys(scores).length > 0 && (
        <div style={D.scoreRow}>
          {Object.entries(scores).map(([k, v]) => (
            <div key={k} style={D.scoreItem}>
              <span style={D.scoreLabel}>{k.charAt(0).toUpperCase() + k.slice(1)}</span>
              <span style={{ ...D.scoreVal, color: scoreColor(v) }}>{v}/10</span>
            </div>
          ))}
          {showScore && <div style={D.scoreSummary}>Got past the gatekeeper ✓</div>}
        </div>
      )}
    </div>
  );
}

const D = {
  demoWrap: { background: '#fff', border: '1px solid #E5E7EB', borderRadius: 14, overflow: 'hidden', boxShadow: '0 12px 32px rgba(0,0,0,0.10)', width: '100%', maxWidth: 440 },
  demoHeader: { display: 'flex', alignItems: 'center', gap: 8, padding: '12px 16px', borderBottom: '1px solid #F1F3F7', background: '#F8F9FB' },
  demoDot: { width: 8, height: 8, borderRadius: '50%', background: '#22C55E', boxShadow: '0 0 0 3px #DCF7E3' },
  demoTitle: { fontSize: 13, fontWeight: 600, color: '#374151', flex: 1 },
  demoBadge: { fontSize: 11, color: '#EF4444', fontWeight: 600 },
  transcript: { padding: '12px 16px', minHeight: 220, maxHeight: 260, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 10 },
  turn: { display: 'flex', flexDirection: 'column', gap: 2 },
  turnUser: { alignItems: 'flex-end' },
  turnAgent: { alignItems: 'flex-start' },
  turnLabel: { fontSize: 10, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 2 },
  labelUser: { color: '#2563EB' },
  labelAgent: { color: '#6B7280' },
  turnText: { fontSize: 13, lineHeight: 1.5, color: '#374151', background: '#F1F3F7', padding: '8px 12px', borderRadius: 10, maxWidth: '85%' },
  scoreRow: { padding: '12px 16px', borderTop: '1px solid #F1F3F7', background: '#F8F9FB', display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' },
  scoreItem: { display: 'flex', flexDirection: 'column', alignItems: 'center', background: '#fff', border: '1px solid #E5E7EB', borderRadius: 8, padding: '6px 12px', minWidth: 64 },
  scoreLabel: { fontSize: 10, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.05em' },
  scoreVal: { fontSize: 16, fontWeight: 700, fontFamily: 'JetBrains Mono, monospace' },
  scoreSummary: { fontSize: 12, color: '#16A34A', fontWeight: 600, marginLeft: 'auto' },
};

// ── Main landing page ──────────────────────────────────────────────────────────

const PERSONAS = [
  { emoji: '🛡️', name: 'Maria', title: 'Front Desk', industry: 'Dental Clinic', color: '#EFF6FF', border: '#BFDBFE', text: '#1D4ED8' },
  { emoji: '🦷', name: 'Dr. Park', title: 'Clinic Owner', industry: 'Dental Clinic', color: '#F0FDF4', border: '#BBF7D0', text: '#15803D' },
  { emoji: '💼', name: 'Rachel', title: 'Exec Assistant', industry: 'SaaS Company', color: '#FFF7ED', border: '#FED7AA', text: '#C2410C' },
  { emoji: '📊', name: 'Jennifer', title: 'VP of Sales', industry: 'SaaS Company', color: '#FAF5FF', border: '#E9D5FF', text: '#7E22CE' },
  { emoji: '🏠', name: 'Carol', title: 'Broker/Owner', industry: 'Real Estate', color: '#ECFDF5', border: '#A7F3D0', text: '#065F46' },
  { emoji: '🔐', name: 'David', title: 'IT Manager', industry: 'Enterprise', color: '#FEF2F2', border: '#FECACA', text: '#B91C1C' },
];

const STEPS = [
  { n: '1', title: 'Build your scenario', body: 'Choose an industry and persona, or pick from our template library. Customize the objections you want to practice against.' },
  { n: '2', title: 'We call you', body: 'Hit "Practice Now", enter your number, and your phone rings in seconds. No dialing, no setup — just answer and pitch.' },
  { n: '3', title: 'Get your scorecard', body: 'After you hang up, see exactly how you did — scored against Gong, RAIN Group, and HubSpot benchmarks.' },
];

const STATS = [
  { val: '10×', label: 'more practice calls than roleplay' },
  { val: '6', label: 'dimensions scored per call' },
  { val: '11', label: 'industry personas ready to use' },
  { val: '<3s', label: 'to receive your practice call' },
];

export default function Landing() {
  const nav = useNavigate();

  return (
    <div style={L.page}>
	<style>{`
        @media (max-width: 768px) {
          .calliope-nav-links a:not(.nav-cta-mobile-show) { display: none; }
        }
      `}</style>
      {/* Beta banner */}
      <div style={L.betaBanner}>🎉 You're using Calliope Beta</div>

      {/* Nav */}
      <nav style={L.nav}>
        <div style={L.navInner}>
          <div style={L.logo}>🎙️ <span style={L.logoText}>Calliope</span></div>
          <div className="calliope-nav-links" style={L.navLinks}>
            <a href="#how" style={L.navLink}>How it works</a>
            <a href="#personas" style={L.navLink}>Personas</a>
            <a href="#pricing" style={L.navLink}>Pricing</a>
	    <a href="https://orronai.com/resources/resources.html" style={L.navLink}>Resources</a>
            <button style={L.navLogin} onClick={() => nav('/login')}>Sign in</button>
            <button style={L.navCta} onClick={() => nav('/login')}>Try free →</button>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section style={L.hero}>
        <div style={L.heroInner}>
          <div style={L.heroLeft}>
            <div style={L.eyebrow}>Cold call training for sales teams</div>
            <h1 style={L.h1}>
              Practice your pitch<br />
              <span style={L.h1Accent}>before it counts.</span>
            </h1>
            <p style={L.heroBody}>
              Calliope calls your phone with a realistic AI persona — skeptical gatekeeper, 
              ROI-focused owner, or IT manager. You pitch. We score you against 
              industry benchmarks. Repeat until you're ready.
            </p>
            <div style={L.heroCtas}>
              <button style={L.ctaPrimary} onClick={() => nav('/login')}>
                Start practicing free
              </button>
              <span style={L.ctaNote}>3 free calls · no credit card</span>
            </div>
            {/* Stats row */}
            <div style={L.statsRow}>
              {STATS.map((s, i) => (
                <div key={i} style={L.stat}>
                  <span style={L.statVal}>{s.val}</span>
                  <span style={L.statLabel}>{s.label}</span>
                </div>
              ))}
            </div>
          </div>
          <div style={L.heroRight}>
            <AnimatedDemo />
          </div>
        </div>
      </section>

      {/* How it works */}
      <section id="how" style={L.section}>
        <div style={L.sectionInner}>
          <div style={L.sectionEyebrow}>How it works</div>
          <h2 style={L.h2}>From zero to pitch-ready in one afternoon.</h2>
          <div style={L.steps}>
            {STEPS.map((s, i) => (
              <div key={i} style={L.step}>
                <div style={L.stepNum}>{s.n}</div>
                <div>
                  <div style={L.stepTitle}>{s.title}</div>
                  <div style={L.stepBody}>{s.body}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Personas */}
      <section id="personas" style={{ ...L.section, background: '#fff' }}>
        <div style={L.sectionInner}>
          <div style={L.sectionEyebrow}>Persona library</div>
          <h2 style={L.h2}>11 realistic personas across 7 industries.</h2>
          <p style={L.sectionSub}>Every persona has real objections, a distinct personality, and a voice. Build your own or start from a template.</p>
          <div style={L.personaGrid}>
            {PERSONAS.map((p, i) => (
              <div key={i} style={{ ...L.personaCard, background: p.color, borderColor: p.border }}>
                <span style={L.personaEmoji}>{p.emoji}</span>
                <div style={{ ...L.personaName, color: p.text }}>{p.name}</div>
                <div style={L.personaTitle}>{p.title}</div>
                <div style={{ ...L.personaIndustry, color: p.text, background: p.border + '60' }}>{p.industry}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Scoring */}
      <section style={L.section}>
        <div style={L.sectionInner}>
          <div style={L.sectionEyebrow}>Scoring</div>
          <h2 style={L.h2}>Benchmarked against what actually works.</h2>
          <p style={L.sectionSub}>Every call is scored on 6 dimensions using research from Gong, RAIN Group, and HubSpot — the same data sales teams pay thousands for.</p>
          <div style={L.scoreGrid}>
            {[
              { dim: 'Hook', bench: 'Top performers hook in <15 sec', src: 'Gong' },
              { dim: 'Clarity', bench: 'Value prop clear in <30 sec', src: 'RAIN Group' },
              { dim: 'Objections', bench: 'Answer objections with a question', src: 'Gong' },
              { dim: 'Articulation', bench: 'No filler words, confident pacing', src: 'RAIN Group' },
              { dim: 'Listening', bench: '57% listen vs 43% talk ratio', src: 'Gong' },
              { dim: 'Close', bench: "64% of reps never ask for a next step", src: 'HubSpot' },
            ].map((s, i) => (
              <div key={i} style={L.scoreCard}>
                <div style={L.scoreDim}>{s.dim}</div>
                <div style={L.scoreBench}>{s.bench}</div>
                <div style={L.scoreSrc}>{s.src}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" style={{ ...L.section, background: '#fff' }}>
        <div style={L.sectionInner}>
          <div style={L.sectionEyebrow}>Pricing</div>
          <h2 style={L.h2}>Start free. Upgrade when you're ready.</h2>
          <div style={{ ...L.pricingGrid, justifyContent: 'center', maxWidth: 700, margin: '0 auto' }}>
            <div style={L.pricingCard}>
              <div style={L.planName}>Free</div>
              <div style={L.planPrice}>$0</div>
              <div style={L.planPer}>forever</div>
              <ul style={L.planFeatures}>
                {['3 practice calls/month', '3 scenarios', 'Full scorecard after each call', 'Template persona library'].map((f, i) => (
                  <li key={i} style={L.planFeature}><span style={L.check}>✓</span>{f}</li>
                ))}
              </ul>
              <button style={L.planCta} onClick={() => nav('/login')}>Get started free</button>
            </div>
            <div style={{ ...L.pricingCard, ...L.pricingCardFeatured }}>
              <div style={L.featuredBadge}>Most popular</div>
              <div style={{ ...L.planName, color: '#fff' }}>Full Access</div>
              <div style={{ ...L.planPrice, color: '#fff' }}>$19</div>
              <div style={{ ...L.planPer, color: '#93C5FD' }}>per month</div>
              <ul style={L.planFeatures}>
                {['Unlimited practice calls', 'Unlimited scenarios', 'Full scorecard + coach feedback', 'Progress tracking over time', 'All 11 personas + custom builder'].map((f, i) => (
                  <li key={i} style={{ ...L.planFeature, color: '#E0F2FE' }}><span style={{ ...L.check, color: '#4ADE80' }}>✓</span>{f}</li>
                ))}
              </ul>
              <button style={{ ...L.planCta, ...L.planCtaFeatured }} onClick={() => nav('/login')}>Start practicing</button>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section style={{ ...L.section, background: '#1D4ED8', padding: '80px 24px' }}>
        <div style={{ ...L.sectionInner, textAlign: 'center' }}>
          <h2 style={{ ...L.h2, color: '#fff', marginBottom: 12 }}>Stop burning real prospects to practice.</h2>
          <p style={{ color: '#BFDBFE', fontSize: 17, marginBottom: 32, maxWidth: 480, margin: '0 auto 32px' }}>
            Your first 3 calls are free. No credit card, no commitment.
          </p>
          <button style={{ ...L.ctaPrimary, background: '#fff', color: '#1D4ED8', fontSize: 16, padding: '14px 32px' }}
            onClick={() => nav('/login')}>
            Start practicing now →
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer style={L.footer}>
        <div style={L.footerInner}>
          <span style={L.footerLogo}>🎙️ Calliope</span>
          <span style={L.footerText}>© 2026 Calliope, a product by OrronAI.</span>
        </div>
      </footer>
    </div>
  );
}

const L = {
  page: { minHeight: '100vh', background: '#F8F9FB', fontFamily: 'Inter, -apple-system, sans-serif' },
  betaBanner: { background: '#1D4ED8', color: '#fff', textAlign: 'center', padding: '8px 16px', fontSize: 13, fontWeight: 600 },

  // Nav
  nav: { position: 'sticky', top: 0, background: 'rgba(255,255,255,0.92)', backdropFilter: 'blur(8px)', borderBottom: '1px solid #E5E7EB', zIndex: 100 },
  navInner: { maxWidth: 1100, margin: '0 auto', padding: '0 24px', height: 60, display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  logo: { display: 'flex', alignItems: 'center', gap: 8, fontSize: 20 },
  logoText: { fontWeight: 700, color: '#111827', letterSpacing: '-0.02em' },
  byline: { fontSize: 11, color: '#9CA3AF', fontWeight: 500, marginLeft: 8 },
  navLinks: { display: 'flex', alignItems: 'center', gap: 24 },
  navLink: { fontSize: 14, color: '#6B7280', textDecoration: 'none', fontWeight: 500 },
  navLogin: { fontSize: 14, color: '#374151', background: 'transparent', border: 'none', cursor: 'pointer', fontWeight: 500 },
  navCta: { fontSize: 14, color: '#fff', background: '#2563EB', border: 'none', borderRadius: 8, padding: '8px 18px', cursor: 'pointer', fontWeight: 600 },

  // Hero
  hero: { padding: '80px 24px 60px', background: '#fff', borderBottom: '1px solid #E5E7EB' },
  heroInner: { maxWidth: 1100, margin: '0 auto', display: 'flex', alignItems: 'center', gap: 64, flexWrap: 'wrap' },
  heroLeft: { flex: '1 1 420px' },
  heroRight: { flex: '1 1 380px', display: 'flex', justifyContent: 'center' },
  eyebrow: { fontSize: 13, fontWeight: 600, color: '#2563EB', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 16 },
  h1: { fontSize: 52, fontWeight: 800, lineHeight: 1.1, letterSpacing: '-0.03em', color: '#111827', marginBottom: 20 },
  h1Accent: { color: '#2563EB' },
  heroBody: { fontSize: 17, color: '#4B5563', lineHeight: 1.7, marginBottom: 32, maxWidth: 480 },
  heroCtas: { display: 'flex', alignItems: 'center', gap: 16, marginBottom: 40, flexWrap: 'wrap' },
  ctaPrimary: { fontSize: 15, fontWeight: 600, color: '#fff', background: '#2563EB', border: 'none', borderRadius: 10, padding: '13px 28px', cursor: 'pointer' },
  ctaNote: { fontSize: 13, color: '#9CA3AF' },
  statsRow: { display: 'flex', gap: 32, flexWrap: 'wrap' },
  stat: { display: 'flex', flexDirection: 'column' },
  statVal: { fontSize: 24, fontWeight: 800, color: '#111827', fontFamily: 'JetBrains Mono, monospace', letterSpacing: '-0.02em' },
  statLabel: { fontSize: 12, color: '#9CA3AF', marginTop: 2 },

  // Sections
  section: { padding: '80px 24px' },
  sectionInner: { maxWidth: 1100, margin: '0 auto' },
  sectionEyebrow: { fontSize: 13, fontWeight: 600, color: '#2563EB', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 12 },
  h2: { fontSize: 36, fontWeight: 800, letterSpacing: '-0.02em', color: '#111827', marginBottom: 16, lineHeight: 1.2 },
  sectionSub: { fontSize: 16, color: '#6B7280', marginBottom: 40, maxWidth: 560, lineHeight: 1.7 },

  // Steps
  steps: { display: 'flex', gap: 32, flexWrap: 'wrap' },
  step: { flex: '1 1 280px', display: 'flex', gap: 20, alignItems: 'flex-start' },
  stepNum: { width: 40, height: 40, borderRadius: '50%', background: '#EFF6FF', color: '#2563EB', fontWeight: 800, fontSize: 18, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontFamily: 'JetBrains Mono, monospace' },
  stepTitle: { fontSize: 16, fontWeight: 700, color: '#111827', marginBottom: 6 },
  stepBody: { fontSize: 14, color: '#6B7280', lineHeight: 1.65 },

  // Personas
  personaGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 12 },
  personaCard: { border: '1px solid', borderRadius: 12, padding: '20px 16px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 },
  personaEmoji: { fontSize: 28, marginBottom: 4 },
  personaName: { fontSize: 15, fontWeight: 700 },
  personaTitle: { fontSize: 12, color: '#6B7280' },
  personaIndustry: { fontSize: 11, fontWeight: 600, padding: '3px 8px', borderRadius: 999, marginTop: 6 },

  // Score grid
  scoreGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 12 },
  scoreCard: { background: '#fff', border: '1px solid #E5E7EB', borderRadius: 12, padding: '18px 20px' },
  scoreDim: { fontSize: 15, fontWeight: 700, color: '#111827', marginBottom: 4 },
  scoreBench: { fontSize: 13, color: '#4B5563', lineHeight: 1.5, marginBottom: 8 },
  scoreSrc: { fontSize: 11, fontWeight: 600, color: '#2563EB', textTransform: 'uppercase', letterSpacing: '0.06em' },

  // Pricing
  pricingGrid: { display: 'flex', gap: 20, flexWrap: 'wrap', alignItems: 'flex-start' },
  pricingCard: { flex: '1 1 280px', background: '#F8F9FB', border: '1px solid #E5E7EB', borderRadius: 16, padding: '32px 28px', position: 'relative' },
  pricingCardFeatured: { background: '#1D4ED8', border: '1px solid #1D4ED8', boxShadow: '0 12px 40px rgba(29,78,216,0.25)' },
  featuredBadge: { position: 'absolute', top: -12, left: '50%', transform: 'translateX(-50%)', background: '#22C55E', color: '#fff', fontSize: 11, fontWeight: 700, padding: '4px 12px', borderRadius: 999, whiteSpace: 'nowrap' },
  planName: { fontSize: 14, fontWeight: 600, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 },
  planPrice: { fontSize: 48, fontWeight: 800, color: '#111827', letterSpacing: '-0.03em', fontFamily: 'JetBrains Mono, monospace', lineHeight: 1 },
  planPer: { fontSize: 13, color: '#9CA3AF', marginBottom: 24, marginTop: 4 },
  planFeatures: { listStyle: 'none', marginBottom: 28, display: 'flex', flexDirection: 'column', gap: 10 },
  planFeature: { display: 'flex', gap: 8, fontSize: 14, color: '#374151', alignItems: 'flex-start' },
  check: { color: '#22C55E', fontWeight: 700, flexShrink: 0 },
  planCta: { width: '100%', padding: '12px', background: '#2563EB', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 600, fontSize: 14, cursor: 'pointer' },
  planCtaFeatured: { background: '#fff', color: '#1D4ED8' },
  planCtaOutline: { background: 'transparent', color: '#2563EB', border: '1.5px solid #2563EB' },

  // Footer
  footer: { borderTop: '1px solid #E5E7EB', padding: '24px', background: '#fff' },
  footerInner: { maxWidth: 1100, margin: '0 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  footerLogo: { fontWeight: 700, color: '#111827' },
  footerText: { fontSize: 13, color: '#9CA3AF' },
};
